import React, { useEffect, useState } from 'react';
import { Package, TrendingUp, DollarSign, RefreshCw, AlertTriangle, ChevronRight, ShoppingBag, Star, BarChart2 } from 'lucide-react';
import axios from 'axios';
import { motion } from 'framer-motion';

const API_STATS = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/inventario/dashboard-stats/`;

export default function TenantDashboard({ tenant }) {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchStats = async () => {
        setLoading(true);
        try {
            const res = await axios.get(API_STATS);
            setStats(res.data);
        } catch (e) {
            console.error('Error fetching dashboard stats', e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchStats(); }, []);

    if (loading) return (
        <div className="cargando">
            <RefreshCw className="animate-spin text-brand-800" size={32} />
        </div>
    );

    const semana = stats?.ventas_semana || [];
    const maxSemana = Math.max(...semana.map(d => d.total), 1);
    const totalSemana = semana.reduce((a, d) => a + d.total, 0);

    return (
        <div className="space-y-8 pb-10">
            {/* Cabecera */}
            <div className="flex justify-between items-end">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-white">Panel de Control</h1>
                    <p className="mt-1 text-slate-500 dark:text-slate-400">
                        Resumen operativo de <span className="text-brand-800 dark:text-brand-400 font-bold">{tenant || sessionStorage.getItem('tenant_name')}</span>
                    </p>
                </div>
                <button
                    onClick={fetchStats}
                    className="p-2.5 rounded-xl border transition-all text-slate-500 border-slate-200 hover:bg-slate-100 hover:text-slate-700 dark:bg-white/5 dark:border-white/10 dark:text-slate-400 dark:hover:text-white dark:hover:bg-white/10"
                >
                    <RefreshCw size={20} />
                </button>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <CardKPI title="Ventas Hoy"        value={`Gs. ${(stats?.ventas_hoy || 0).toLocaleString()}`}          subtitle={`${stats?.cantidad_ventas_hoy || 0} transacciones`}   icon={<DollarSign size={24} />}    color="marca" />
                <CardKPI title="Productos Activos" value={stats?.total_productos || 0}                                  subtitle="Items en catálogo"                                      icon={<Package size={24} />}       color="marca" />
                <CardKPI title="Inversión Semanal" value={`Gs. ${(stats?.compras_semana || 0).toLocaleString()}`}       subtitle="Compras a proveedores"                                  icon={<ShoppingBag size={24} />}   color="marca" />
                <CardKPI title="Stock Crítico"     value={stats?.stock_critico_count || 0}                              subtitle="Productos bajo el mínimo"                               icon={<AlertTriangle size={24} />} color="problema" />
            </div>

            {/* Gráfico semanal + Top productos */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
                {/* Ventas últimos 7 días */}
                <div className="xl:col-span-2 bg-white border border-slate-200 shadow-sm dark:bg-gray-900/60 dark:border-white/10 dark:shadow-2xl rounded-3xl p-6 backdrop-blur-md">
                    <div className="flex justify-between items-center mb-6">
                        <h3 className="font-bold flex items-center gap-2 text-slate-900 dark:text-white">
                            <BarChart2 className="text-brand-800" size={20} />
                            Ventas — Últimos 7 Días
                        </h3>
                        <span className="text-xs font-bold text-exito-600 dark:text-exito-400 bg-exito-50 dark:bg-exito-500/10 border border-exito-200 dark:border-exito-500/20 px-2 py-1 rounded-lg">
                            Total: Gs. {totalSemana.toLocaleString()}
                        </span>
                    </div>

                    {totalSemana === 0 ? (
                        <div className="flex flex-col items-center justify-center h-48 text-slate-400 dark:text-slate-600">
                            <BarChart2 size={48} className="opacity-20 mb-3" />
                            <p className="text-sm">Sin ventas en los últimos 7 días</p>
                        </div>
                    ) : (
                        <div className="flex items-end gap-3 h-48">
                            {semana.map((dia, i) => {
                                const pct = maxSemana > 0 ? (dia.total / maxSemana) * 100 : 0;
                                const isToday = i === semana.length - 1;
                                return (
                                    <div key={i} className="flex-1 flex flex-col items-center gap-2">
                                        <span className="text-[10px] font-bold text-slate-400 dark:text-slate-500">
                                            {dia.total > 0 ? `${(dia.total / 1000).toFixed(0)}K` : ''}
                                        </span>
                                        <div className="w-full flex items-end justify-center" style={{ height: '120px' }}>
                                            <motion.div
                                                initial={{ height: 0 }}
                                                animate={{ height: `${Math.max(pct, dia.total > 0 ? 4 : 0)}%` }}
                                                transition={{ duration: 0.6, delay: i * 0.06 }}
                                                className={`w-full rounded-t-lg transition-colors ${
                                                    isToday
                                                        ? 'bg-brand-500 shadow-lg shadow-brand-500/30'
                                                        : 'bg-slate-200 hover:bg-slate-300 dark:bg-white/15 dark:hover:bg-white/25'
                                                }`}
                                            />
                                        </div>
                                        <span className={`text-[10px] font-bold ${isToday ? 'text-brand-800 dark:text-brand-400' : 'text-slate-400 dark:text-slate-600'}`}>
                                            {dia.fecha}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Top Productos */}
                <div className="bg-white border border-slate-200 shadow-sm dark:bg-gray-900/60 dark:border-white/10 dark:shadow-2xl rounded-3xl p-6 backdrop-blur-md h-fit">
                    <h3 className="font-bold mb-6 flex items-center gap-2 text-slate-900 dark:text-white">
                        <Star className="text-brand-800 dark:text-brand-400" size={20} />
                        Más Vendidos
                    </h3>
                    <div className="space-y-4">
                        {stats?.top_productos?.length > 0 ? (
                            stats.top_productos.map((p, i) => (
                                <div key={i} className="flex items-center gap-3">
                                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center font-bold text-xs shrink-0 ${i === 0 ? 'bg-alerta-400 text-black' : 'bg-slate-100 text-slate-500 dark:bg-white/5 dark:text-slate-400'}`}>
                                        {i + 1}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-bold truncate text-slate-900 dark:text-white">{p.producto__nombre}</p>
                                        <p className="text-xs font-medium text-brand-800 dark:text-brand-400">{p.total_vendido} uds vendidas</p>
                                    </div>
                                    <TrendingUp size={12} className="text-exito-500 shrink-0" />
                                </div>
                            ))
                        ) : (
                            <p className="text-center italic py-10 text-slate-400 dark:text-slate-600">Sin ventas aún.</p>
                        )}
                    </div>
                </div>
            </div>

            {/* Stock Crítico */}
            <div className="bg-white border border-slate-200 shadow-sm dark:bg-gray-900/60 dark:border-white/10 dark:shadow-2xl rounded-3xl overflow-hidden backdrop-blur-md">
                <div className="p-6 border-b flex justify-between items-center bg-error-50/50 dark:bg-error-500/5 border-slate-200 dark:border-white/10">
                    <div className="flex items-center gap-2">
                        <AlertTriangle className="text-error-500" size={20} />
                        <h3 className="font-bold text-slate-900 dark:text-white">Alertas de Stock Bajo</h3>
                    </div>
                    {stats?.stock_critico_count > 0 && (
                        <span className="text-xs font-bold text-error-600 dark:text-error-500 bg-error-100 dark:bg-error-500/10 px-2 py-1 rounded-full uppercase">
                            {stats.stock_critico_count} productos
                        </span>
                    )}
                </div>
                <div className="tabla-filas">
                    {stats?.stock_critico_list?.length > 0 ? (
                        stats.stock_critico_list.map(p => (
                            <div key={p.id} className="p-4 flex justify-between items-center hover:bg-slate-50 dark:hover:bg-white/5 transition-colors group">
                                <div>
                                    <p className="font-bold text-slate-900 dark:text-white">{p.nombre}</p>
                                    <p className="text-xs text-slate-400 dark:text-slate-500">Mínimo: {p.min} unidades</p>
                                </div>
                                <div className="flex items-center gap-6">
                                    <div className="text-right">
                                        <p className="text-sm font-bold text-error-500 dark:text-error-400">{p.stock} uds disponibles</p>
                                        <div className="w-24 h-1.5 bg-slate-200 dark:bg-white/5 rounded-full mt-1 overflow-hidden">
                                            <div className="h-full bg-error-500 transition-all" style={{ width: `${Math.min((p.stock / p.min) * 100, 100)}%` }} />
                                        </div>
                                    </div>
                                    <ChevronRight size={18} className="text-slate-300 dark:text-slate-700 group-hover:text-slate-600 dark:group-hover:text-white transition-colors" />
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="p-16 text-center text-slate-400 dark:text-slate-500">
                            <Package size={48} className="mx-auto mb-4 opacity-10" />
                            <p>No hay productos con stock crítico.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function CardKPI({ title, value, subtitle, icon, color }) {
    const colors = {
        marca:    'bg-brand-50 border-brand-200 text-brand-800 dark:bg-brand-500/10 dark:border-brand-500/20 dark:text-brand-400',
        problema: 'bg-error-50 border-error-200 text-error-700 dark:bg-error-500/10 dark:border-error-500/20 dark:text-error-400',
        neutro:   'bg-slate-50 border-slate-200 text-slate-700 dark:bg-slate-800/60 dark:border-white/10 dark:text-slate-300',
    };

    return (
        <motion.div
            whileHover={{ y: -4 }}
            className={`p-6 rounded-3xl border backdrop-blur-sm relative overflow-hidden ${colors[color]}`}
        >
            <div className="flex justify-between items-start">
                <div>
                    <p className="text-xs font-bold uppercase tracking-wider opacity-70 mb-1">{title}</p>
                    <h4 className="titulo-pagina">{value}</h4>
                    <p className="text-[10px] opacity-60 mt-1 font-medium">{subtitle}</p>
                </div>
                <div className="p-2 opacity-80">{icon}</div>
            </div>
            <div className="absolute -bottom-2 -right-2 opacity-5 scale-150 rotate-12">{icon}</div>
        </motion.div>
    );
}
